import { inject, Pipe, PipeTransform } from '@angular/core';
import { LocaleService } from '../services/locale.service';

export enum TimeAgoStyle {
  LONG = 'long',
  SHORT = 'short',
  NARROW = 'narrow'
}

const MS_PER_SECOND = 1000;
const MS_PER_MINUTE = MS_PER_SECOND * 60;
const MS_PER_HOUR = MS_PER_MINUTE * 60;
const MS_PER_DAY = MS_PER_HOUR * 24;

const resolveUnit = (elapsed: number): { unit: Intl.RelativeTimeFormatUnit; delta: number } => {
  const abs: number = Math.abs(elapsed);

  if (abs < MS_PER_MINUTE) {
    return { unit: 'second', delta: Math.trunc(elapsed / MS_PER_SECOND) };
  }

  if (abs < MS_PER_HOUR) {
    return { unit: 'minute', delta: Math.trunc(elapsed / MS_PER_MINUTE) };
  }

  if (abs < MS_PER_DAY) {
    return { unit: 'hour', delta: Math.trunc(elapsed / MS_PER_HOUR) };
  }

  return { unit: 'day', delta: Math.trunc(elapsed / MS_PER_DAY) };
};

export const getTimeAgo = (value: number, locale: string, style: TimeAgoStyle = TimeAgoStyle.SHORT): string | null => {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return null;
  }

  const elapsed: number = Date.now() - value;
  const { unit, delta } = resolveUnit(elapsed);

  return new Intl.RelativeTimeFormat(locale, { numeric: 'auto', style }).format(-delta, unit);
};

/** Bare, preposition-free elapsed duration (e.g. `1 hr` / `1 Std.`) for `for`/`seit`-style templates. */
export const getDuration = (value: number, locale: string): string | null => {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return null;
  }

  const elapsed: number = Date.now() - value;
  const { unit, delta } = resolveUnit(elapsed);

  return new Intl.NumberFormat(locale, { style: 'unit', unit, unitDisplay: 'short' }).format(Math.abs(delta));
};

@Pipe({
  standalone: true,
  name: 'timeAgo'
})
export class TimeAgoPipe implements PipeTransform {
  private readonly localeService: LocaleService = inject(LocaleService);

  transform(value: number, style: TimeAgoStyle = TimeAgoStyle.SHORT): string | null {
    return getTimeAgo(value, this.localeService.locale(), style);
  }
}
